import * as Application from 'expo-application';
import Constants from 'expo-constants';
import * as Device from 'expo-device';
import { Platform } from 'react-native';
import { v4 as uuid } from 'uuid';
import { APITypes } from '../../primary-record-types';
import { getBrowserInfo } from './browser';
import { enqueueAnalyticsEvent } from './queue';
import { reportAnalyticsError } from './reportError';
import { getActiveSession, startSession, stopSession } from './session';

/**
 * Session boundary event names. Pinpoint emitted these two and the warehouse filters on them by literal
 * value, so they have to survive the migration unchanged.
 */
const SESSION_START_EVENT = '_session.start';
const SESSION_STOP_EVENT = '_session.stop';

const MAX_ATTRIBUTE_VALUE_LENGTH = 1024;
const ATTRIBUTE_KEY_PATTERN = /^[A-Za-z0-9._-]+$/;

/**
 * Bring attributes inside the limits the request schema enforces.
 *
 * Attribute values come from user input — a pasted portal search term, for instance — and a single event
 * that violates a limit fails validation for the whole request it travels in, taking unrelated events
 * with it.
 */
const sanitizeAttributes = (attributes: Record<string, string>): Record<string, string> =>
    Object.fromEntries(
        Object.entries(attributes)
            .filter(([key]) => ATTRIBUTE_KEY_PATTERN.test(key))
            .map(([key, value]) => [key, value.slice(0, MAX_ATTRIBUTE_VALUE_LENGTH)]),
    );

/** The app ships only to iOS, Android and the web. */
const PLATFORM: APITypes.analyticsService.AnalyticsClientPlatform =
    Platform.OS === 'ios' || Platform.OS === 'android' ? Platform.OS : 'web';

const enqueue = (
    eventType: string,
    session: APITypes.analyticsService.AnalyticsEventSession,
    attributes?: Record<string, string>,
) => {
    const event: APITypes.analyticsService.AnalyticsEventInput = {
        eventId: uuid(),
        eventType,
        eventTimestamp: new Date().toISOString(),
        platform: PLATFORM,
        session,
    };

    const appVersion = Constants.expoConfig?.version;
    if (appVersion) event.appVersion = appVersion;

    if (PLATFORM !== 'web') {
        // The native build number tells apart store builds that share a marketing version.
        const appBuild = Application.nativeBuildVersion;
        if (appBuild) event.appBuild = appBuild;
        // The release version on both platforms. Platform.Version would give Android's API level instead.
        const osVersion = Device.osVersion;
        if (osVersion) event.osVersion = osVersion;
    }

    // deviceMake and deviceModel keep the meaning they have in the warehouse today — the vendor string
    // and the browser name — so existing groupings on those columns continue to work. The version is
    // new information and travels as an attribute rather than displacing the browser name. Only a
    // browser has these: React Native defines a `navigator` global too, but it describes no browser.
    const browser = PLATFORM === 'web' ? getBrowserInfo() : undefined;
    if (browser) {
        if (browser.vendor) event.deviceMake = browser.vendor;
        event.deviceModel = browser.name;
    }

    // No userId: the service resolves it from the caller's Cognito identity, so one supplied here would
    // be stripped anyway.
    const merged = { ...attributes, ...(browser && { browserVersion: browser.version }) };
    if (Object.keys(merged).length) event.attributes = sanitizeAttributes(merged);

    enqueueAnalyticsEvent(event);
};

/**
 * Open a session and announce it.
 *
 * Sessions are only ever created through here, so every session id in the warehouse has a start event
 * to go with it — including one opened implicitly by an event that arrives while the tab is hidden, for
 * instance from a request that resolves after the user switched away.
 */
const beginSession = () => {
    const session = startSession();
    enqueue(SESSION_START_EVENT, session);
    return session;
};

const stopCurrentSession = () => {
    const endedSession = stopSession();
    if (endedSession) enqueue(SESSION_STOP_EVENT, endedSession);
};

const recordBestEffort = (action: () => void) => {
    try {
        action();
    } catch (error) {
        reportAnalyticsError('Failed to record analytics', error);
    }
};

/** Record an ordinary event, opening a session first if none is currently open. */
export const recordAnalyticsEvent = (eventType: string, attributes?: Record<string, string>) => {
    recordBestEffort(() => enqueue(eventType, getActiveSession() ?? beginSession(), attributes));
};

/** Close whatever session is open, if any. */
export const recordSessionStop = () => {
    recordBestEffort(stopCurrentSession);
};

/**
 * Start a session, closing any that is still open first. Closing before opening is what keeps starts and
 * stops paired when a session was opened implicitly and the tab then becomes visible.
 */
export const recordSessionStart = () => {
    recordBestEffort(() => {
        stopCurrentSession();
        beginSession();
    });
};
